import React, { useEffect, useState } from 'react';
import { CalendarDays, Check, TriangleAlert } from 'lucide-react';
import { apiErrorMessage, trailService } from '../../services/api';
import { Button, Modal } from '../ui';
import { RESOURCE_TYPES, SEALS, STATUS, TRAIL_TYPES, WEEKDAYS, fmtHoursRound, isoDate } from '../../utils/trails';
import './trails.css';

/** Pill do tipo da trilha: "CERTIFICAÇÃO", com o ícone. */
export function TypePill({ type, onDark = false }) {
  const t = TRAIL_TYPES[type] || TRAIL_TYPES.certification;
  return (
    <span className={`tr-type ${onDark ? 'tr-type--dark' : ''}`}>
      <t.icon size={12} strokeWidth={2.4} aria-hidden="true" />{t.label}
    </span>
  );
}

// Selos que marcam itens do checklist, não fontes (não entram nos selects do editor)
const FLAG_SEALS = { forbidden: { label: 'Proibido' } };

/** Selo da fonte: OFICIAL, COMPLEMENTAR, RELATO, PRÁTICA; e PROIBIDO no checklist. */
export function SealPill({ seal, className = '' }) {
  const s = SEALS[seal] || FLAG_SEALS[seal] || SEALS.official;
  return <span className={`tr-seal tr-seal--${seal} ${className}`}>{s.label}</span>;
}

/** Status da etapa ou do recurso: Concluído, Pendente, Atrasado, Bloqueado. */
export function StatusPill({ status, label }) {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span className={`tr-status tr-status--${s.tone}`}>
      <s.icon size={12} strokeWidth={2.6} aria-hidden="true" />{label || s.label}
    </span>
  );
}

/** Tópico do exame com a faixa de peso. */
export function WeightChip({ name, label }) {
  return (
    <span className="tr-weight">
      {name}<span className="tr-weight__pct">{label}</span>
    </span>
  );
}

/** Barra de pesos: um segmento por tópico, com largura pelo peso e cor da etapa que cobre. */
export function WeightBar({ weights }) {
  const total = weights.reduce((s, w) => s + w.weight, 0) || 1;
  return (
    <div className="tr-wbar" role="img" aria-label="Pesos dos tópicos do exame, pela etapa que cobre cada um">
      {weights.map(w => (
        <span key={w.name} style={{ width: `${(w.weight / total) * 100}%`, background: w.color }} title={`${w.name} · ${w.label}`} />
      ))}
    </div>
  );
}

/** Ícone de 40px do recurso: interno em fundo de marca com ícone lime; externo em tint. */
export function ResourceIcon({ type, open = false, size = 40 }) {
  const t = RESOURCE_TYPES[type] || RESOURCE_TYPES.link;
  return (
    <span
      className={`tr-ricon ${t.internal ? 'tr-ricon--internal' : ''} ${open ? 'tr-ricon--open' : ''}`}
      style={{ width: size, height: size }}
    >
      <t.icon size={Math.round(size * 0.45)} strokeWidth={2.1} aria-hidden="true" />
    </span>
  );
}

export function MiniProgress({ pct, className = '' }) {
  return (
    <span className={`tr-mini ${className}`}>
      <span className="tr-mini__track"><span style={{ width: `${pct}%` }} /></span>
      <span className="tr-mini__pct">{pct}%</span>
    </span>
  );
}

const HOUR_OPTIONS = [2, 3, 4, 5, 6, 8];

/**
 * Disponibilidade: horas por dia, dias da semana e data da prova.
 * value = { hours, weekdays: [1..7], examDate, noDate }
 */
export function AvailabilityForm({ value, onChange, compact = false }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const toggleDay = (n) => {
    const has = value.weekdays.includes(n);
    if (has && value.weekdays.length === 1) return;
    set({ weekdays: has ? value.weekdays.filter(d => d !== n) : [...value.weekdays, n].sort() });
  };
  return (
    <div className={`tr-avail ${compact ? 'tr-avail--compact' : ''}`}>
      <span className="tr-avail__label" id="tr-hours">Horas por dia</span>
      <div className="tr-seg" role="radiogroup" aria-labelledby="tr-hours">
        {HOUR_OPTIONS.map(h => (
          <button key={h} type="button" role="radio" aria-checked={value.hours === h}
                  className={value.hours === h ? 'is-active' : ''} onClick={() => set({ hours: h })}>
            {h}h
          </button>
        ))}
      </div>

      <span className="tr-avail__label" id="tr-days">Dias da semana</span>
      <div className="tr-days" role="group" aria-labelledby="tr-days">
        {WEEKDAYS.map(d => (
          <button key={d.n} type="button" aria-pressed={value.weekdays.includes(d.n)}
                  className={value.weekdays.includes(d.n) ? 'is-active' : ''} onClick={() => toggleDay(d.n)}>
            {d.short}
          </button>
        ))}
      </div>

      <span className="tr-avail__label">Data da prova</span>
      <div className="tr-examdate">
        <label className={`tr-date ${value.noDate ? 'is-off' : ''}`}>
          <CalendarDays size={16} aria-hidden="true" />
          <input
            type="date"
            value={value.examDate || ''}
            min={isoDate(1)}
            onChange={(e) => set({ examDate: e.target.value, noDate: !e.target.value })}
            aria-label="Data da prova"
          />
        </label>
        <button type="button" className={`tr-nodate ${value.noDate ? 'is-active' : ''}`} aria-pressed={value.noDate}
                onClick={() => set({ noDate: !value.noDate, examDate: value.noDate ? value.examDate : '' })}>
          {value.noDate && <Check size={16} aria-hidden="true" />}Ainda não marquei
        </button>
      </div>
    </div>
  );
}

/** Prévia do plano: total de dias, quadradinhos por semana e quando fica pronto para a prova. */
export function PlanPreview({ preview, examDate }) {
  if (!preview) return <div className="tr-preview tr-preview--loading" aria-busy="true" />;
  const missing = examDate && preview.missingMinutes > 0 ? preview.missingMinutes : 0;
  return (
    <div className="tr-preview">
      <span className="tr-preview__eyebrow">Seu plano</span>
      <span className="tr-preview__days"><strong>{preview.days}</strong> dias</span>
      <span className="tr-preview__calc">{fmtHoursRound(preview.totalMinutes)} ÷ {preview.hoursPerDay}h, {preview.weekdaysLabel}</span>
      <div className="tr-preview__weeks">
        {preview.weeks.map(w => (
          <div key={w.label} className="tr-preview__week">
            <span>{w.label}</span>
            <span className="tr-preview__dots" aria-hidden="true">
              {Array.from({ length: w.count }, (_, i) => <i key={i} />)}
            </span>
            <strong>{w.count}</strong>
          </div>
        ))}
      </div>
      <div className="tr-preview__foot">
        <span>{preview.startLabel}</span>
        <strong>Pronto para a prova em {preview.readyLabel}</strong>
        {!examDate && <span className="tr-preview__hint">Marque a prova a partir de {preview.suggestLabel}.</span>}
      </div>
      {missing > 0 && (
        <p className="tr-preview__warn" role="alert">
          <TriangleAlert size={15} aria-hidden="true" />
          Faltam {fmtHoursRound(missing)}. Aumente as horas por dia ou mude a data.
        </p>
      )}
    </div>
  );
}

/** Prévia que acompanha o formulário de disponibilidade (com um pequeno atraso para não pedir a cada clique). */
export function usePlanPreview(slug, value) {
  const [preview, setPreview] = useState(null);
  const key = `${slug}|${value.hours}|${value.weekdays.join(',')}|${value.noDate ? '' : value.examDate || ''}`;
  useEffect(() => {
    if (!slug) return undefined;
    const timer = setTimeout(() => {
      trailService.preview(slug, {
        hours: value.hours,
        weekdays: value.weekdays.join(','),
        examDate: value.noDate || !value.examDate ? undefined : value.examDate,
      }).then(setPreview).catch(() => setPreview(null));
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return preview;
}

export function toEnrollBody(value, examTime) {
  return {
    hoursPerDay: value.hours,
    weekdays: value.weekdays,
    examDate: value.noDate || !value.examDate ? null : value.examDate,
    examTime: examTime || null,
  };
}

/**
 * "Começar trilha" e "Replanejar": disponibilidade com a prévia do plano.
 * mode: enroll | replan
 */
export function AvailabilityModal({ open, onClose, trail, mode = 'enroll', initial, onDone }) {
  const [value, setValue] = useState(initial || { hours: 4, weekdays: [1, 2, 3, 4, 5], examDate: '', noDate: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const preview = usePlanPreview(open ? trail?.slug : null, value);

  useEffect(() => {
    if (open) {
      setValue(initial || { hours: 4, weekdays: [1, 2, 3, 4, 5], examDate: '', noDate: true });
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (mode === 'enroll') await trailService.enroll(trail.slug, toEnrollBody(value));
      else await trailService.updateEnrollment(trail.slug, toEnrollBody(value), true);
      onDone?.();
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível salvar o plano.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={820}
      title={mode === 'enroll' ? 'Começar trilha' : 'Replanejar'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}>{mode === 'enroll' ? 'Montar meu plano' : 'Replanejar'}</Button>
        </>
      )}
    >
      <p className="tr-modal__lead">
        {mode === 'enroll'
          ? 'Com isso o plano da trilha é distribuído no calendário. Dá para mudar depois em Replanejar.'
          : 'O plano recomeça hoje com o que falta, no novo limite de horas e nos dias escolhidos.'}
      </p>
      <div className="tr-modal__grid">
        <AvailabilityForm value={value} onChange={setValue} compact />
        <PlanPreview preview={preview} examDate={value.noDate ? null : value.examDate} />
      </div>
      {error && <p className="tr-error" role="alert">{error}</p>}
    </Modal>
  );
}
